import { useCallback } from 'react'
import { useProjectMemberRepository } from '../../../../services/adaptors/projectMemberRepositoryAdaptor'
import { useTeamRepository } from '../../../../services/adaptors/teamRepositoryAdaptor'
import {
  useBooleanParser,
  useDateParser,
  useDateTimeParser,
  useMultiLineTextParser,
  useNumberParser,
  useReferencedEntityParser,
  useSelectParser,
  useTextParser,
} from './changeLogParser'
import { WbsItemSelectOptions } from './wbsItemSelectOptions'
import { WbsItemAdditionalPropertyLayoutEntity } from '../../../../domain/entity/WbsItemAdditionalPropertyLayoutEntity'
import { useWbsItemRepository } from '../../../../services/adaptors/wbsItemRepositoryAdaptor'
import { useWbsItemAdditionalProperyValueChangeLogParser } from './wbsItemAdditionalPropertyValueChangeLogParser'
import { useUserRepository } from '../../../../services/adaptors/userRepositoryAdaptor'
import { useTicketRepository } from '../../../../services/adaptors/ticketRepositoryAdaptor'
import { useSprintRepository } from '../../../../services/adaptors/sprintRepositoryAdaptor'
import { ChangeLogValueParser } from '../model/changeLog'

export const useWbsItemChangeLogParser = (
  { subStatuses, difficulties, priorities }: WbsItemSelectOptions,
  wbsItemAdditionalProperties: WbsItemAdditionalPropertyLayoutEntity | undefined
) => {
  const booleanParser = useBooleanParser()
  const numberParser = useNumberParser()
  const textParser = useTextParser()
  const multiLineTextParser = useMultiLineTextParser()
  const dateParser = useDateParser()
  const dateTimeParser = useDateTimeParser()
  const statusParser = useStatusParser()
  const subStatusParser = useSelectParser(subStatuses)
  const difficultyParser = useSelectParser(difficulties)
  const priorityParser = useSelectParser(priorities)
  const { fetchAsReferencedEntity: fetchTeam } = useTeamRepository()
  const teamParser = useReferencedEntityParser(fetchTeam)
  const { fetchAsReferencedEntity: fetchProjectMember } =
    useProjectMemberRepository()
  const projectMemberParser = useReferencedEntityParser(fetchProjectMember)
  const { fetchAsReferencedEntity: fetchUser } = useUserRepository()
  const userParser = useReferencedEntityParser(fetchUser)
  const { fetchAsReferencedEntity: fetchWbsItem } = useWbsItemRepository()
  const wbsItemParser = useReferencedEntityParser(fetchWbsItem)
  const { fetchAsReferencedEntity: fetchTicket } = useTicketRepository()
  const ticketParser = useReferencedEntityParser(fetchTicket)
  const { fetchAsReferencedEntity: fetchSprint } = useSprintRepository()
  const sprintParser = useReferencedEntityParser(fetchSprint)
  const {
    isWbsItemAdditionalPropertyValueChangeLog,
    wbsItemAdditionalPropertyValueParsers,
  } = useWbsItemAdditionalProperyValueChangeLogParser(
    wbsItemAdditionalProperties,
    booleanParser,
    numberParser,
    textParser,
    multiLineTextParser,
    dateParser,
    dateTimeParser,
    teamParser,
    userParser,
    wbsItemParser,
    ticketParser
  )
  const getChangeLogParser = useCallback(
    (path: string) => {
      switch (path) {
        case 'code':
        case 'displayName':
          return textParser
        case 'description':
        case 'startIf':
        case 'completeIf':
          return multiLineTextParser
        case 'status':
          return statusParser
        case 'substatus':
          return subStatusParser
        case 'difficulty':
          return difficultyParser
        case 'priority':
          return priorityParser
        case 'teamUuid':
          return teamParser
        case 'sprintUuid':
          return sprintParser
        case 'accountableUuid':
        case 'responsibleUuid':
        case 'assigneeUuid':
          return projectMemberParser
        case 'estimatedStoryPoint':
        case 'estimatedHour':
          return numberParser
        default:
          if (isWbsItemAdditionalPropertyValueChangeLog(path)) {
            return wbsItemAdditionalPropertyValueParsers[path]
          } else {
            return textParser
          }
      }
    },
    [
      difficultyParser,
      priorityParser,
      projectMemberParser,
      subStatusParser,
      statusParser,
      teamParser,
      sprintParser,
      numberParser,
      textParser,
      multiLineTextParser,
      isWbsItemAdditionalPropertyValueChangeLog,
      wbsItemAdditionalPropertyValueParsers,
    ]
  )
  return getChangeLogParser
}

const useStatusParser = () => {
  const statusParser = useCallback<ChangeLogValueParser>((status?: string) => {
    return status ? status.replaceAll('"', '') : status
  }, [])

  return statusParser
}
